import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ChevronDown, MapPin, Phone, Clock, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import MenuGallery from "@/components/MenuGallery";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

export default function LandingPage() {
  const navigate = useNavigate();
  const [showMenuGallery, setShowMenuGallery] = useState(false);

  const handleOrderNow = () => {
    // Set flag to show pizza promo on hub page
    sessionStorage.setItem("showPizzaPromo", "true");
    navigate("/hub", { state: { fromLanding: true } });
  };

  const scrollToInfo = () => {
    window.scrollTo({ top: window.innerHeight * 0.8, behavior: "smooth" });
  };

  return (
    <div 
      className="flex flex-col"
      style={{ backgroundColor: "#1A4D4D" }}
    >
      {/* Hero Section - Fixed height viewport */}
      <div className="min-h-screen relative flex flex-col items-center justify-center overflow-hidden">
        {/* Background gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, #1A4D4D 0%, #0F3333 50%, #1A4D4D 100%)"
          }}
        />
        
        {/* Decorative circles */}
        <div 
          className="absolute top-10 left-10 w-32 h-32 rounded-full opacity-10"
          style={{ backgroundColor: "#59C3C3" }}
        />
        <div 
          className="absolute bottom-20 right-10 w-48 h-48 rounded-full opacity-10"
          style={{ backgroundColor: "#F28C38" }}
        />
        <div 
          className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full opacity-5"
          style={{ backgroundColor: "#59C3C3" }}
        />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center px-6 text-center" data-testid="order-now-section">
          {/* Logo in a styled container */}
          <div 
            className="mb-6 p-3 rounded-2xl shadow-2xl"
            style={{ backgroundColor: "white" }}
          >
            <img
              src={LOGO_URL}
              alt="Dauphin Island Food Mart"
              className="w-48 sm:w-56 md:w-64 h-auto object-contain"
              data-testid="landing-logo"
              loading="eager"
              fetchPriority="high"
            />
          </div>
          
          {/* Tagline */}
          <p 
            className="text-lg sm:text-xl font-medium mb-2 tracking-wide"
            style={{ 
              color: "#59C3C3",
              fontFamily: "Montserrat, sans-serif"
            }}
          >
            Fresh Food • Cold Drinks • Island Vibes
          </p>
          
          {/* Subtitle */}
          <p 
            className="text-sm opacity-70 mb-8"
            style={{ color: "white" }}
          >
            Your favorite island convenience store
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            {/* Order Now Button */}
            <Button
              data-testid="order-now-btn"
              onClick={handleOrderNow}
              className="order-btn px-10 py-6 text-lg font-bold rounded-full transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center gap-3"
              style={{
                backgroundColor: "#F28C38",
                color: "white",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <ShoppingBag className="h-5 w-5" />
              Order Now
            </Button>
            
            {/* Check Our Menu Button */}
            <Button
              data-testid="check-menu-btn"
              onClick={() => setShowMenuGallery(true)}
              variant="outline"
              className="px-8 py-6 text-lg font-bold rounded-full transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center gap-3 border-2"
              style={{
                borderColor: "#59C3C3",
                backgroundColor: "transparent",
                color: "#59C3C3",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <BookOpen className="h-5 w-5" />
              Check Our Menu
            </Button>
          </div>
          
          {/* Order type hint */}
          <p 
            className="mt-3 text-sm opacity-50"
            style={{ color: "white" }}
          >
            Pick-up / To-Go Orders
          </p>
        </div>

        {/* Scroll indicator */}
        <button 
          onClick={scrollToInfo}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/50 hover:text-white/80 transition-colors cursor-pointer"
          data-testid="scroll-indicator"
        >
          <span className="text-xs">Learn More</span>
          <ChevronDown className="h-5 w-5 animate-bounce" />
        </button>
      </div>

      {/* Additional Content Section - Scrollable Area */}
      <div className="min-h-screen py-16 px-6" style={{ backgroundColor: "#0F3333" }}>
        <div className="max-w-4xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-16">
            <h2 
              className="text-2xl sm:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: "Montserrat" }}
            >
              Welcome to Dauphin Island
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Your one-stop shop for fresh food, cold drinks, and island essentials. 
              We've been serving the Dauphin Island community with quality products and friendly service.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {/* Feature 1 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div 
                className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: "#F28C38" }}
              >
                <ShoppingBag className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Montserrat" }}>
                Fresh Food
              </h3>
              <p className="text-sm text-gray-400">
                Krispy Krunchy Chicken, Hunt Brothers Pizza, and House Burgers made fresh daily.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div 
                className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: "#59C3C3" }}
              >
                <Clock className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Montserrat" }}>
                Convenient Hours
              </h3>
              <p className="text-sm text-gray-400">
                Open early at 5 AM for your morning needs. Extended hours on weekends.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div 
                className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: "#1A4D4D" }}
              >
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Montserrat" }}>
                Easy Ordering
              </h3>
              <p className="text-sm text-gray-400">
                Order online for quick pickup. No waiting in line - your food ready when you arrive.
              </p>
            </div>
          </div>

          {/* Location Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "Montserrat" }}>
              Visit Us Today
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-300">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" style={{ color: "#59C3C3" }} />
                <span className="text-sm">1000 Bienville Blvd, Dauphin Island, AL</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" style={{ color: "#F28C38" }} />
                <a href="tel:+12518612320" className="text-sm hover:text-white transition-colors">
                  (251) 861-2320
                </a>
              </div>
            </div>
            
            {/* CTA Button */}
            <Button
              data-testid="landing-order-btn-bottom"
              onClick={handleOrderNow}
              className="mt-8 px-8 py-4 text-base font-bold rounded-full transition-all hover:scale-105 active:scale-95"
              style={{
                backgroundColor: "#F28C38",
                color: "white",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              Start Your Order
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Menu Gallery Modal */}
      <MenuGallery open={showMenuGallery} onClose={() => setShowMenuGallery(false)} />
    </div>
  );
}
